import { useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  Flex,
  FlexColumn,
  Chip,
  Menu,
  IconButton,
  Dialog,
} from '@elementary/common-ui/components';
import { List, Column } from '@elementary/common-ui/components/Table';
import { ReactComponent as MoreHorizIcon } from '@elementary/common-ui/assets/more.svg';
import { ReactComponent as EditIcon } from '@elementary/common-ui/assets/20x20/edit.svg';
import { ReactComponent as DeleteIcon } from '@elementary/common-ui/assets/20x20/trash.svg';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { ROLE_ID } from '@elementary/common-ui/constants/urlParams';
import { Role } from '../../shared/types';
import RoleDrawer from './RoleDrawer';

const Name = styled(Flex)`
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: ${({ theme }) => theme.palette.common.black};
  gap: 8px;
`;

const NameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

interface RolesTableProps {
  roles?: Role[];
  search: string;
  onDeleteRole: (roleId: string) => Promise<void>;
  isDeleting: boolean;
}

const RolesTable = ({
  roles = [],
  search,
  onDeleteRole,
  isDeleting,
}: RolesTableProps) => {
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [, setSearchParams] = useSearchParams();

  const filteredData = useMemo(
    () =>
      search.trim()
        ? roles.filter((item) =>
            item.name.toLowerCase().includes(search.toLowerCase()),
          )
        : roles,
    [roles, search],
  );

  const columns: Array<Column<Role>> = useMemo(
    () => [
      {
        id: 'name',
        header: 'Role',
        filterable: false,
        renderer: ({ name, builtin }) => (
          <FlexColumn sx={{ gap: 0.5, overflow: 'hidden' }}>
            <Name>
              <NameText>{name}</NameText>
              {builtin && (
                <Chip
                  label="Default"
                  variant="outlined"
                  size="medium"
                  bold
                  disabled
                />
              )}
            </Name>
          </FlexColumn>
        ),
        width: 300,
      },
      {
        id: 'description',
        header: 'Description',
        filterable: false,
        sortable: false,
      },

      {
        id: 'menu',
        header: '',
        renderer: (role) =>
          !role.builtin ? (
            <Menu
              closeOnChange
              menuButton={
                <IconButton height="20px">
                  <MoreHorizIcon />
                </IconButton>
              }
              options={[
                { label: 'Edit', value: 'edit', icon: <EditIcon /> },
                { label: 'Delete', value: 'delete', icon: <DeleteIcon /> },
              ]}
              onChange={(value) => {
                if (value === 'edit') {
                  setSearchParams((prev) => {
                    prev.set(ROLE_ID, role.id);
                    return prev;
                  });
                } else if (value === 'delete') {
                  setRoleToDelete(role);
                }
              }}
            />
          ) : (
            <div />
          ),
        filterable: false,
        sortable: false,
        metaColumn: true,
      },
    ],
    [setRoleToDelete, setSearchParams],
  );

  return (
    <>
      <List
        columns={columns}
        data={filteredData}
        defaultSortKey="name"
        onRowClick={(role) => {
          setSearchParams((prev) => {
            prev.set(ROLE_ID, role.id);
            return prev;
          });
        }}
      />

      <RoleDrawer
        onClose={() => {
          setSearchParams((prev) => {
            prev.delete(ROLE_ID);
            return prev;
          });
        }}
      />

      <Dialog
        open={!!roleToDelete}
        onClose={() => setRoleToDelete(null)}
        onConfirmButtonClick={async () => {
          if (roleToDelete) {
            await onDeleteRole(roleToDelete.id);
            setRoleToDelete(null);
          }
        }}
        isLoading={isDeleting}
        titleIcon={<DeleteIcon />}
        title="Delete role"
        confirmText="Delete role"
        message={`Are you sure you want to delete "${roleToDelete?.name}"?`}
        secondaryMessage="This action cannot be undone."
        variant="error"
      />
    </>
  );
};

export default RolesTable;
